import { Service } from "typedi";
import { JwtDecoder } from "../../../shared/services/jwt-decoder/JwtDecoder";
import { CurrencyConverter } from "../../../shared/services/currency/CurrencyConverter";
import { IStJwtPayload } from "../../../application/core/models/IStJwtPayload";

export interface IJwtPriceResult {
  totalPrice: string;
  currencyCode: string;
}

/**
 * Extracts the transaction price from the JWT, preferring mainamount (major units)
 * over baseamount (minor units). This ensures the Google Pay wallet sheet always
 * reflects the price from the most recently issued JWT, even if the merchant
 * hasn't configured any callbacks.
 */
@Service()
export class GooglePayJwtPriceResolver {
  constructor(
    private jwtDecoder: JwtDecoder,
    private currencyConverter: CurrencyConverter,
  ) {}

  resolve(jwt: string): IJwtPriceResult | null {
    if (!jwt) {
      return null;
    }

    const { payload } = this.jwtDecoder.decode<IStJwtPayload>(jwt);
    const currencyCode = payload.currencyiso3a;

    if (!currencyCode) {
      return null;
    }

    if (payload.mainamount != null && String(payload.mainamount) !== "") {
      return {
        totalPrice: this.currencyConverter.formatAmount(
          String(payload.mainamount),
          currencyCode,
        ),
        currencyCode,
      };
    }

    // baseamount is in minor units — conversion handles zero-decimal (JPY)
    // and three-decimal (OMR, BHD) currencies automatically
    if (payload.baseamount != null && String(payload.baseamount) !== "") {
      return {
        totalPrice: this.currencyConverter.convertFromMinorUnits(
          String(payload.baseamount),
          currencyCode,
        ),
        currencyCode,
      };
    }

    return null;
  }
}
